import { Alert, Link as MuiLink } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { PageState } from '../../shared/data/PageState';
import { Timeline } from '../../shared/detail/Timeline';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { toPageError } from '../partners/PartnerListPage';
import { memberRepositoryToken } from './repository';

export function AssignmentHistoryPanel({ memberId }: { readonly memberId: string }) {
  const repository = useRepository(memberRepositoryToken);
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.listAssignments(memberId, signal),
    [memberId, refreshKey, repository],
    (items) => items.length === 0,
  );
  return (
    <PageState
      emptyMessage={t('members.assignments.empty')}
      error={toPageError(resource.error, t('members.errors.assignments'))}
      onRetry={() => setRefreshKey((key) => key + 1)}
      status={resource.status}
    >
      {resource.data === null ? null : (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('members.assignments.timelineNote')}
          </Alert>
          <Timeline
            items={resource.data.map((item) => {
              const from = formatTimestamp(item.effectiveFrom, 'Asia/Bangkok', language);
              const to =
                item.effectiveTo === null
                  ? null
                  : formatTimestamp(item.effectiveTo, 'Asia/Bangkok', language);
              return {
                id: item.id,
                title: `${item.phase} · ${item.agent.code} · ${item.assignmentType}`,
                timestamp: `${from.display} (${from.originalOffset})${to === null ? '' : ` → ${to.display} (${to.originalOffset})`}`,
                description: (
                  <>
                    {item.reason}
                    {item.changeSetId === null ? null : (
                      <>
                        {' '}
                        ·{' '}
                        <MuiLink
                          component={Link}
                          to={`/member-assignment-change-sets/${item.changeSetId}`}
                        >
                          {t('members.assignments.changeSet')}
                        </MuiLink>
                      </>
                    )}
                  </>
                ),
              };
            })}
          />
        </>
      )}
    </PageState>
  );
}
